import { ethers } from "ethers"
import { ArtBlocksChainId } from "contractConfig"
import { makeChainReadCacheKey, readThroughChainCache } from "utils/chainReadCache"
import { getReadOnlyProvider } from "utils/readOnlyProvider"

const ERC721_METADATA_ABI = [
  "function ownerOf(uint256 tokenId) view returns (address)",
  "function tokenURI(uint256 tokenId) view returns (string)",
]

export interface Erc721TokenChainData {
  ownerAddress?: string
  tokenURI?: string
}

const getErc721Contract = (contractAddress: string, chainId: ArtBlocksChainId) =>
  new ethers.Contract(contractAddress, ERC721_METADATA_ABI, getReadOnlyProvider(chainId))

export const fetchErc721Owner = async (
  contractAddress: string,
  tokenId: string,
  chainId: ArtBlocksChainId = 1
) => {
  return readThroughChainCache(
    makeChainReadCacheKey("erc721-owner", chainId, contractAddress, tokenId),
    async () => {
      const contract = getErc721Contract(contractAddress, chainId)
      return contract.ownerOf(tokenId) as Promise<string>
    }
  )
}

export const fetchErc721TokenChainData = async (
  contractAddress: string,
  tokenId: string,
  chainId: ArtBlocksChainId = 1
): Promise<Erc721TokenChainData> => {
  return readThroughChainCache(
    makeChainReadCacheKey("erc721-token-data", chainId, contractAddress, tokenId),
    async () => {
      const contract = getErc721Contract(contractAddress, chainId)
      const [ownerResult, tokenURIResult] = await Promise.allSettled([
        contract.ownerOf(tokenId) as Promise<string>,
        contract.tokenURI(tokenId) as Promise<string>,
      ])

      return {
        ownerAddress: ownerResult.status === "fulfilled" ? ownerResult.value : undefined,
        tokenURI: tokenURIResult.status === "fulfilled" ? tokenURIResult.value : undefined,
      }
    }
  )
}
